import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { videoCategories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";

interface FormResponse {
  code?: string;
  meta?: { message?: string; detail?: string };
  message?: string;
}

const FORM_ACTION = "https://readdy.ai/api/form/db73ljun6nb9f68e98ug";

const services = [
  {
    icon: "ri-play-circle-line",
    title: "多元内容浏览",
    text: "汇聚短视频、网络电影与娱乐综艺，覆盖搞笑、美食、科幻、真人秀等丰富题材，满足不同口味的观看需求。",
  },
  {
    icon: "ri-filter-3-line",
    title: "精准分类筛选",
    text: "按类别、地区、年份多维筛选，并支持按时间、点赞、评论排序，帮你快速定位想看的内容。",
  },
  {
    icon: "ri-search-eye-line",
    title: "便捷搜索发现",
    text: "支持按标题与简介关键词模糊搜索，配合热门榜单与相关内容推荐，让每一次浏览都有新发现。",
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 樱桃视频",
    "了解樱桃视频的平台定位、服务范围与联系方式。樱桃视频致力于为用户提供清晰、便捷的视频内容浏览体验。"
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value);
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (response.ok && code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络连接后重试。");
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <header className="mt-5 max-w-3xl">
        <span className="inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-label text-accent-900">
          关于平台
        </span>
        <h1 className="mt-4 text-2xl md:text-4xl font-heading font-bold text-foreground-950">
          关于樱桃视频
        </h1>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
          樱桃视频是集最新影视资源、精彩短视频、娱乐综艺及网络电影为一体的综合视频内容平台。
          我们面向喜欢观看多样视频内容的年轻群体与影视爱好者，致力于提供高清、多元、便捷的内容浏览体验，
          帮助用户快速查找、筛选并发现感兴趣的精彩内容。
        </p>
      </header>

      <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
        {services.map((item) => (
          <div
            key={item.title}
            className="rounded-lg border border-background-200 bg-background-50 p-6"
          >
            <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${item.icon} text-2xl`}></i>
            </span>
            <h2 className="mt-4 text-base font-heading font-semibold text-foreground-950">
              {item.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
          </div>
        ))}
      </section>

      <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            内容分类
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            平台目前设有以下主要分类，每个分类下细分多个子类别，方便按兴趣浏览。
          </p>
          <div className="mt-5 space-y-4">
            {videoCategories.map((category) => (
              <Link
                key={category.slug}
                to={`/category/${category.slug}`}
                className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
              >
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i
                    className={
                      category.slug === "short-video"
                        ? "ri-play-circle-line"
                        : category.slug === "net-movie"
                        ? "ri-film-line"
                        : "ri-mic-line"
                    }
                  ></i>
                </span>
                <div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900">
                    {category.name}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                    {category.description}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {category.subCategories.map((sub) => (
                      <span
                        key={sub}
                        className="rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-600"
                      >
                        {sub}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            联系我们
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            内容合作、版权反馈或建议意见，欢迎通过下方表单或邮箱与我们联系。
          </p>

          <div className="mt-5 space-y-3">
            <div className="flex items-center gap-3 rounded-lg bg-background-100 px-4 py-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-primary-600">
                <i className="ri-mail-line"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">合作邮箱</p>
                <p className="text-sm text-foreground-800">partner@cherry-video.example.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg bg-background-100 px-4 py-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-primary-600">
                <i className="ri-customer-service-2-line"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">内容反馈</p>
                <p className="text-sm text-foreground-800">feedback@cherry-video.example.com</p>
              </div>
            </div>
          </div>

          <form
            id="about-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
          >
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm text-foreground-700" htmlFor="contact-name">
                  称呼
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入您的称呼"
                  className="w-full rounded-md bg-background-100 border border-background-200 px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-foreground-700" htmlFor="contact-email">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="w-full rounded-md bg-background-100 border border-background-200 px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-foreground-700" htmlFor="contact-topic">
                  联系类型
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  defaultValue="内容合作"
                  className="w-full rounded-md bg-background-100 border border-background-200 px-4 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors cursor-pointer"
                >
                  <option value="内容合作">内容合作</option>
                  <option value="版权反馈">版权反馈</option>
                  <option value="功能建议">功能建议</option>
                  <option value="其他">其他</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-foreground-700" htmlFor="contact-message">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  required
                  placeholder="请简要描述您的需求（不超过 500 字）"
                  className="w-full rounded-md bg-background-100 border border-background-200 px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none"
                ></textarea>
              </div>

              <div className="cherry-newsletter-note" aria-hidden="true">
                <input
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full rounded-md bg-primary-500 py-3 text-sm font-label font-medium text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
              >
                {status === "submitting" ? "提交中..." : "提交信息"}
              </button>

              {status === "success" && (
                <p className="flex items-center gap-2 rounded-md bg-accent-100 px-4 py-2.5 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line"></i>
                  提交成功，感谢你的留言，我们会尽快与你联系。
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-2 rounded-md bg-primary-100 px-4 py-2.5 text-sm text-primary-900">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}
            </div>
          </form>
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-lg bg-background-100 p-6">
          <h2 className="text-base font-heading font-semibold text-foreground-950">法律声明</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            本站为演示性质的内容展示站点，页面中所展示的视频封面、标题与简介均为虚构演示素材，
            不涉及任何真实作品的播放、传播或下载。站内暂不提供视频播放与下载功能，仅用于内容浏览与筛选演示。
          </p>
        </div>
        <div className="rounded-lg bg-background-100 p-6">
          <h2 className="text-base font-heading font-semibold text-foreground-950">隐私政策</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            我们重视你的隐私。通过联系表单提交的信息仅用于回复你的咨询，不会用于其他用途，
            也不会向第三方出售或共享。你可以随时通过上方邮箱联系我们，请求删除已提交的信息。
          </p>
        </div>
      </section>
    </div>
  );
}